import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import type { useRoute } from 'nuxt/app';
import { uuid4 } from '~~/common/utils';
import { useQueueStore } from '~~/common/stores/queue';
import { useUserStore } from '~~/common/stores/user';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { setLoginCookies } from 'awServerModule/runtime/util/grantsHelper';
import { feApiEndpoint } from 'awServerModule/runtime/util/consts';
import { PUSH_LOGOUT } from '~~/common/plugins/aw-analytics';
import {
  AGT_PASSWORD,
  AGT_ANONYMOUS,
  AGT_REFRESH_TOKEN,
  AGT_THIRD_PARTY_ACCESS_TOKEN,
} from '~~/common/config/AuthGrantType';
import { AP_SALESFORCE } from '~~/common/config/AuthProvider';
import type { TokenResponse } from '~~/common/plugins/axios';

type PwdPayload = {
  username: string;
  password: string;
  grant_type: typeof AGT_PASSWORD;
};
type SalesforcePayload = {
  token: string;
  provider: typeof AP_SALESFORCE;
  grant_type: typeof AGT_THIRD_PARTY_ACCESS_TOKEN;
};
type AnonymousPayload = {
  grant_type: typeof AGT_ANONYMOUS;
};

export const useAuthenticationStore = defineStore('authentication', () => {
  const nuxtApp = useNuxtApp();
  const queueStore = useQueueStore();
  const userStore = useUserStore();
  const userInterfaceStore = useUserInterfaceStore();

  const customerDiamondLogoutIframeKey = ref<false | string>(false);

  function feLogin (payload: AnonymousPayload | PwdPayload | SalesforcePayload) {
    const { $feApiNetwork } = nuxtApp;
    return $feApiNetwork.$post<TokenResponse>('/get-token', payload);
  }

  function setCustomerDiamondLogoutIframeKey () {
    customerDiamondLogoutIframeKey.value = `aw_${uuid4()}`;
  }

  function loginWithUsernameAndPassword (payload: Omit<PwdPayload, 'grant_type'>) {
    return feLogin({
      ...payload,
      grant_type: AGT_PASSWORD,
    });
  }

  function loginWithSalesForce (data: ReturnType<typeof useRoute>['query']) {
    return feLogin({
      token: String(data.code),
      provider: AP_SALESFORCE,
      grant_type: AGT_THIRD_PARTY_ACCESS_TOKEN,
    });
  }

  async function loginAnonymous () {
    const { $axiosRefreshTokens } = nuxtApp;
    return $axiosRefreshTokens({
      grant_type: AGT_ANONYMOUS,
    });
  }

  async function loginWithRefreshToken () {
    const { $axiosRefreshTokens } = nuxtApp;
    return $axiosRefreshTokens({
      grant_type: AGT_REFRESH_TOKEN,
    });
  }

  async function logout ({ accountDelete } = { accountDelete: false }) {
    userInterfaceStore.startLoading({ id: 'logout' });
    return await queueStore.add({
      action: logoutRequest,
      actionName: 'authentication/logoutRequest',
      payload: { accountDelete },
      canStartSecondAction: true,
    });
  }

  async function clearAuthCookies () {
    const {
      $cookies,
      $logger,
    } = nuxtApp;

    const { isChangeRequiresServer } = setLoginCookies({
      cookies: $cookies,
      body: {
        access_token: undefined,
        token_type: undefined,
        refresh_token: undefined,
        expires_in: undefined,
        login_type: undefined,
      },
    });
    $cookies.remove('login_type', {
      path: '/',
      sameSite: 'lax',
    });
    if (isChangeRequiresServer) {
      try {
        await window.fetch(`${feApiEndpoint}/revoke-token`, { method: 'DELETE' });
      } catch (err) {
        $logger.error(err);
      }
    }
  }

  async function logoutRequest ({ accountDelete }: { accountDelete: boolean }) {
    const {
      $cookies,
      $logger,
      $api,
      $awAnalytics,
    } = nuxtApp;
    try {
      $cookies.remove('login_type', {
        path: '/',
        sameSite: 'lax',
      });

      if (!accountDelete) {
        await Promise.allSettled([
          $api.$delete('/token/revoke').catch(err => $logger.error(err)),
          feLogin({ grant_type: AGT_ANONYMOUS }).catch((err) => {
            $logger.error(err);
            clearAuthCookies();
          }),
        ]);
        try {
          const isB2B = userStore.data?.personalInfo?.isB2B;
          if (typeof isB2B !== 'boolean') {
            $logger.warn(`isB2B is not a boolean, but ${typeof isB2B}.`);
          }
          $awAnalytics[PUSH_LOGOUT]({ isB2B: !!isB2B });
        } catch (error) {
          $logger.error(error);
        }
      }

      await userStore.fetchUser({ refreshData: __AW_IS_SHOP__ });
      userStore.resetShoppingListSessionData();
      // await userStore.setShoppingLists( []);
    } catch (error) {
      $logger.error(error);
    } finally {
      userInterfaceStore.endLoading({ id: 'logout' });
    }
  }

  async function customerDiamondLogin ({ path }: { path: string }) {
    userInterfaceStore.startLoading({ id: 'login' });
    return await queueStore.add({
      action: customerDiamondLoginRequest,
      actionName: 'authentication/customerDiamondLoginRequest',
      payload: { path },
    });
  }

  function customerDiamondLoginRequest ({ path }: { path: string }) {
    const {
      $i18n,
      $awRuntimeConfig,
    } = nuxtApp;
    userInterfaceStore.endLoading({ id: 'login' });
    // `path` is encoded: it carries its own query string, and an unescaped `&` in it would end the
    // `state` parameter and turn the rest into top-level parameters of the login URL.
    window.open(`${($awRuntimeConfig.customerDiamondLoginUrl).replace('expid_XX', ('expid_' + $i18n.locale.value))}&state=${encodeURIComponent(path)}`, '_self');
    return Promise.resolve();
  }

  async function customerDiamondRegistration ({ path }: { path: string }) {
    userInterfaceStore.startLoading({ id: 'register' });
    return await queueStore.add({
      action: customerDiamondLoginRequest,
      actionName: 'authentication/customerDiamondLoginRequest',
      payload: { path },
    });
  }

  /*
  function customerDiamondRegistrationRequest ({ path }: { path: string }) {
    const {
      $i18n,
      $awRuntimeConfig,
    } = nuxtApp;
    userInterfaceStore.endLoading({ id: 'register' });
    window.open(`${($awRuntimeConfig.customerDiamondRegisterUrl).replace('expid_XX', ('expid_' + $i18n.locale.value))}&state=${path}`, '_self');
  }
  */

  async function reInitToken () {
    const {
      $cookies,
    } = nuxtApp;
    const accessToken = $cookies.get('access_token');
    const refreshToken = $cookies.get('refresh_token');
    const isServer = import.meta.server;
    if (isServer) {
      if (!accessToken && !refreshToken) {
        await loginAnonymous();
      } else if (!accessToken && refreshToken) {
        await loginWithRefreshToken();
      }
    } else {
      if (!accessToken) {
        await loginWithRefreshToken();
      }
    }
  }

  return {
    customerDiamondLogoutIframeKey,
    setCustomerDiamondLogoutIframeKey,
    loginWithUsernameAndPassword,
    loginWithSalesForce,
    loginAnonymous,
    loginWithRefreshToken,
    logout,
    logoutRequest,
    clearAuthCookies,
    customerDiamondLogin,
    customerDiamondRegistration,
    reInitToken,
  };
});
